import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Navigation, Phone, User, Car, Clock,
  RefreshCw, ArrowLeft, CheckCircle, AlertCircle
} from 'lucide-react';
import LiveTrackingMap from '../../components/maps/LiveTrackingMap';
import {
  formatDistance,
  calculateDistance,
  calculateETA,
  formatCoordinates
} from '../../services/locationService';
import { formatDateTime, formatVehicleName } from '../../lib/utils';
import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const TrackDriver = () => {
  const { trackingCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [connected, setConnected] = useState(false);

  const socketRef = useRef(null);

  // Fetch initial tracking data
  useEffect(() => {
    fetchTrackingData();
  }, [trackingCode]);

  // Setup Socket.IO for real-time updates
  useEffect(() => {
    if (!trackingCode) return;

    socketRef.current = io(SOCKET_URL);

    socketRef.current.on('connect', () => {
      console.log('Socket connected for tracking');
      setConnected(true);
      socketRef.current.emit('join-tracking', trackingCode);
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
    });

    socketRef.current.on('location-update', (data) => {
      console.log('Location update received:', data);
      if (data.location) {
        setDriverLocation({
          latitude: data.location.latitude,
          longitude: data.location.longitude
        });
        setLastUpdated(new Date(data.location.updatedAt || new Date()));
      }
    });

    socketRef.current.on('journey-completed', (data) => {
      setTrackingData(prev => ({
        ...prev,
        status: 'completed'
      }));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave-tracking', trackingCode);
        socketRef.current.disconnect();
      }
    };
  }, [trackingCode]);

  const fetchTrackingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/journeys/track/${trackingCode}`);
      const data = await response.json();

      if (data.success) {
        setTrackingData(data.data);

        if (data.data.currentLocation) {
          setDriverLocation({
            latitude: data.data.currentLocation.latitude,
            longitude: data.data.currentLocation.longitude
          });
          setLastUpdated(new Date(data.data.lastUpdated || new Date()));
        }
      } else {
        setError(data.message || 'Failed to load tracking data');
      }
    } catch (err) {
      console.error('Error fetching tracking data:', err);
      setError('Failed to load tracking data. Please check the tracking code.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate distance to destination
  const getDistanceToDestination = () => {
    if (!driverLocation || !trackingData?.toLocation?.coordinates) return null;

    const distance = calculateDistance(
      driverLocation.latitude,
      driverLocation.longitude,
      trackingData.toLocation.coordinates.latitude,
      trackingData.toLocation.coordinates.longitude
    );

    return distance;
  };

  // Get status display
  const getStatusDisplay = () => {
    if (!trackingData) return null;

    const statusConfig = {
      'scheduled': {
        color: 'bg-yellow-100 text-yellow-800',
        text: 'Trip Scheduled',
        description: 'Driver will start the trip soon'
      },
      'in-progress': {
        color: 'bg-green-100 text-green-800',
        text: 'In Progress',
        description: 'Driver is on the way'
      },
      'completed': {
        color: 'bg-gray-100 text-gray-800',
        text: 'Completed',
        description: 'Trip has been completed'
      },
      'cancelled': {
        color: 'bg-red-100 text-red-800',
        text: 'Cancelled',
        description: 'This trip has been cancelled'
      }
    };

    return statusConfig[trackingData.status] || statusConfig['scheduled'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-10 w-10 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-gray-600">Loading tracking data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-md mx-auto mt-20">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Track</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={fetchTrackingData}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();
  const distanceToDestination = getDistanceToDestination();
  const eta = distanceToDestination ? calculateETA(distanceToDestination) : null;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/track-booking" className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5 mr-2" />
              <span className="text-sm">Back</span>
            </Link>
            <h1 className="text-lg font-bold text-gray-900">Track Driver</h1>
            <div className="flex items-center">
              <span className={`w-2 h-2 rounded-full mr-2 ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-xs text-gray-500">{connected ? 'Live' : 'Offline'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`${statusDisplay.color} px-4 py-3`}>
        <div className="max-w-4xl mx-auto flex items-center">
          {trackingData.status === 'in-progress' ? (
            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3" />
          ) : (
            <CheckCircle className="h-5 w-5 mr-3" />
          )}
          <div>
            <p className="font-semibold">{statusDisplay.text}</p>
            <p className="text-sm opacity-80">{statusDisplay.description}</p>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="relative">
        <LiveTrackingMap
          driverLocation={driverLocation}
          pickupLocation={trackingData?.fromLocation?.coordinates}
          dropLocation={trackingData?.toLocation?.coordinates}
          isTracking={trackingData?.status === 'in-progress' && trackingData?.isTrackingEnabled}
          height="300px"
        />

        {/* ETA Card on Map */}
        {eta && trackingData.status === 'in-progress' && (
          <div className="absolute bottom-4 left-4 bg-white rounded-xl shadow-lg px-4 py-3">
            <p className="text-xs text-gray-500">Estimated Arrival</p>
            <p className="text-2xl font-bold text-blue-600">{eta}</p>
            {distanceToDestination && (
              <p className="text-xs text-gray-500">{formatDistance(distanceToDestination)} away</p>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Driver & Vehicle Info */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <User className="h-7 w-7 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {trackingData.driver?.name || 'Driver'}
                  </h3>
                  <p className="text-sm text-gray-500">Your Driver</p>
                </div>
              </div>
              {trackingData.driver?.phone && (
                <a
                  href={`tel:${trackingData.driver.phone}`}
                  className="p-3 bg-green-500 text-white rounded-full hover:bg-green-600"
                >
                  <Phone className="h-6 w-6" />
                </a>
              )}
            </div>

            {/* Vehicle Info */}
            {trackingData.car && (
              <div className="mt-4 pt-4 border-t flex items-center">
                {trackingData.car.image ? (
                  <img
                    src={trackingData.car.image}
                    alt="Vehicle"
                    className="w-16 h-12 object-cover rounded-lg mr-3"
                  />
                ) : (
                  <div className="w-16 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                    <Car className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">
                    {formatVehicleName(trackingData.car, { showYear: false })}
                  </p>
                  <p className="text-sm text-gray-500">{trackingData.car.registrationNumber}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Route Details */}
        <div className="bg-white rounded-2xl shadow-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Trip Details</h3>

          <div className="space-y-4">
            {/* From */}
            <div className="flex items-start">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <MapPin className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pickup Location</p>
                <p className="font-medium text-gray-900">
                  {trackingData.fromLocation?.address || 'N/A'}
                </p>
              </div>
            </div>

            {/* Connector Line */}
            <div className="ml-5 border-l-2 border-dashed border-gray-200 h-8" />

            {/* To */}
            <div className="flex items-start">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <MapPin className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Drop Location</p>
                <p className="font-medium text-gray-900">
                  {trackingData.toLocation?.address || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Trip Time */}
          {trackingData.startTime && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm text-gray-500">Started at</p>
                  <p className="font-medium">{formatDateTime(trackingData.startTime)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Live Location Info */}
        {driverLocation && trackingData.status === 'in-progress' && (
          <div className="bg-blue-50 rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Navigation className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-800">Live Location</span>
              </div>
              <button
                onClick={fetchTrackingData}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-1 font-mono">
              {formatCoordinates(driverLocation.latitude, driverLocation.longitude)}
            </p>
            {lastUpdated && (
              <p className="text-xs text-blue-500 mt-1">
                Last updated: {formatDateTime(lastUpdated)}
              </p>
            )}
          </div>
        )}

        {/* No Tracking Available */}
        {!trackingData.isTrackingEnabled && trackingData.status === 'in-progress' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="font-medium text-yellow-800">GPS Tracking Not Available</p>
                <p className="text-sm text-yellow-600">
                  The driver has not enabled live GPS tracking for this trip.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tracking Code */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-400">Tracking Code</p>
          <p className="text-lg font-mono font-bold text-gray-600">{trackingCode}</p>
        </div>
      </div>
    </div>
  );
};

export default TrackDriver;
